import formatCurrency from 'core-lib/utils/currency';
import { getImageKitUrl } from 'core-lib/utils/imageKit';
import { CAR_IMAGE_FALLBACK, threeSixtyKeys } from '../constants/newCars';
import {
  AccordionListDataType,
  AccordionListType,
  CityListPopupType,
  ColorVariantListType,
  ColorVariantType,
  ExclusiveFeaturesListType,
  ExclusiveFeaturesType,
  HeroList,
  HeroSectionType,
  KeySpecificationsListType,
  KeySpecificationsType,
  MaintenanceWidgetMapping,
  MaintenanceWidgetType,
  MediaIconsListType,
  MediaIconsType,
  MediaPopupListDataType,
  MediaPopupListType,
  MediaPopupMediaListType,
  MediaPopupType,
  MediaTypes,
  ModelDetailsType,
  MODEL_WIDGETS,
  OverviewType,
  RatingType,
  RCDetailsType,
  SimilarModelListType,
  SpecificationAndFeaturesType,
  SpecificationTabBarListType,
  SpecificationTabBarType,
  VariantDetailsType,
  VariantListPopupMapping,
  VariantListPopupType,
  VariantListsDataType,
  VariantListType,
  VariantSpecificationListType,
  VariantSpecificationType,
  VehicleAlertsType,
  VehicleDetailsType,
  BannerWidgetType,
} from '../types/ModelDetailTypes';
import { serializeSlider } from './serializeLandingPage';

export const serializeHerosSection = (data): HeroSectionType => ({
  widgetType: data?.type,
  title: data?.title,
  shareLink: data?.share_link,
  list: data?.props?.map(
    (item): HeroList => ({
      url: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.url || CAR_IMAGE_FALLBACK,
      }),
      mediaType: item?.media_type || MediaTypes.Image,
    })
  ),
});

export const serializeMediaIcons = (data): MediaIconsType => ({
  widgetType: data?.type,
  list: data?.props?.map(
    (item): MediaIconsListType => ({
      type: item.type,
      title: item?.title,
      icon: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.icon,
      }),
    })
  ),
});

export const serializeOverView = (data): OverviewType => ({
  widgetType: data?.type,
  title: data?.props?.title,
  ratingText: data?.props?.rating_text,
  rating: data?.props?.rating,
  ratingCount: data?.props?.rating_count,
  modelName: data?.props?.model_name,
  discontinued: data?.props?.discontinued,
  parkplusOwners: {
    icon: getImageKitUrl({
      product: 'ImageKit',
      imageUrl: data?.props?.count_of_owners_on_parkplus?.icon,
    }),
    title: data?.props?.count_of_owners_on_parkplus?.title,
  },
  expectedLaunch: data?.props?.expected_launch,
  priceSubtitle: data?.props?.price?.sub_title,
  priceTitle: data?.props?.price?.title,
});

export const serializeSpecificationTabBar = (
  data
): SpecificationTabBarType => ({
  widgetType: data?.type,
  list: data?.props?.map(
    (item): SpecificationTabBarListType => ({
      scrollTo: item?.scroll_to,
      title: item?.title,
    })
  ),
});

export const serializeKeySpecifications = (data): KeySpecificationsType => ({
  widgetType: data?.type,
  title: data?.title,
  list: data?.props?.map(
    (item): KeySpecificationsListType => ({
      title: item?.title,
      subtitle: item?.sub_title,
      image: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.image,
      }),
    })
  ),
});

export const serializeExclusiveFeatures = (data): ExclusiveFeaturesType => ({
  widgetType: data?.type,
  title: data?.title,
  list: data?.props?.map(
    (item): ExclusiveFeaturesListType => ({
      image: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.image,
      }),
      title: item?.title,
    })
  ),
});

export const serializeSpecificationAndFeatures = (
  data
): SpecificationAndFeaturesType => ({
  widgetType: data?.type,
  title: data?.title,
  limit: data?.limit,
  button: {
    variant: data?.cta?.variant,
    title: data?.cta?.title,
    size: data?.cta?.size,
    redirectUrl: data?.cta?.redirection_url,
    filters: data?.cta?.filters,
  },
});

export const serializeVariantList = (data): VariantListType => ({
  widgetType: data?.type,
  limit: data?.limit,
  title: data?.props?.title,
  button: {
    variant: data?.cta?.variant,
    title: data?.cta?.title,
    size: data?.cta?.size,
    redirectUrl: data?.cta?.redirection_url,
    filters: data?.cta?.filters,
  },
});

export const serializeColorVariant = (data): ColorVariantType => ({
  widgetType: data?.type,
  title: data?.props?.title,
  popupTitle: data?.props?.pop_up_title,
  appBarTitle: data?.props?.pop_app_bar_title,
  list: data?.props?.props?.map(
    (item): ColorVariantListType => ({
      carImage: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.car_image || CAR_IMAGE_FALLBACK,
      }),
      colorList: item?.color_list,
      subtitle: item?.sub_title,
      title: item?.title,
    })
  ),
});

export const serializeMaintenanceList = (type, data) => {
  switch (type) {
    case MaintenanceWidgetMapping.Heading:
      return {
        title: data.title,
        type: data.type,
      };
    case MaintenanceWidgetMapping.FuelRangeSlider:
      return {
        maxValueSlider: data?.max_value_slider,
        minValueSlider: data?.min_value_slider,
        resultHeading: data?.result_heading,
        resultSuffix: data?.result_suffix,
        subtitle: data?.sub_title,
        title: data?.title,
        type: data?.type,
      };
    case MaintenanceWidgetMapping.VariantDropdown:
      return {
        title: data?.title,
        type: data?.type,
      };
  }
};

export const serializeMaintenanceWidget = (data): MaintenanceWidgetType => ({
  widgetType: data?.type,
  list: data?.props?.map((item) => serializeMaintenanceList(item?.type, item)),
});

export const serializeRatingWidget = (data): RatingType => ({
  widgetType: data?.type,
  rating: data?.props?.rating,
  ratingCount: data?.props?.rating_count,
  title: data?.props?.title,
  list: data?.props?.props?.map((item) => ({
    title: item?.title,
    rating: item?.rating,
  })),
});

export const serializeCarousel = (data = {}) => {
  const keys = Object.keys(data) || [];
  const obj = {};
  keys.forEach((item) => {
    if (data?.[item]?.data && data?.[item]?.data?.length > 0) {
      (obj[item] as MediaPopupMediaListType) = {
        data: data?.[item]?.data?.map(
          (e): MediaPopupListDataType => ({
            description: e?.description,
            mediaLabel: e?.media_label,
            mediaType: e?.media_type || MediaTypes.Image,
            subtitle: e?.sub_title,
            title: e?.title || '',
            url: getImageKitUrl({
              product: 'ImageKit',
              imageUrl: e?.url || CAR_IMAGE_FALLBACK,
            }),
            thumbnailUrl: getImageKitUrl({
              product: 'ImageKit',
              imageUrl: e?.thumbnail_url,
            }),
          })
        ),
        title: data?.[item]?.title,
      };
    }
  });
  return obj;
};

const serializePanoImages = (data) => {
  return {
    [threeSixtyKeys.Interior]:
      data?.['360']?.interior?.map((item) => item?.url) || [],
    [threeSixtyKeys.Exterior]:
      data?.['360']?.exterior?.map((item) => item?.url) || [],
  };
};

export const serializeMediaPopup = (data): MediaPopupType => ({
  widgetType: data?.type,
  list: data?.props?.map(
    (item): MediaPopupListType => ({
      icon: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.icon,
      }),
      type: item?.type,
      title: item?.title || '',
      carouselType: item?.carousel_type,
      list: item?.props ? serializeCarousel(item?.props) : null,
      panoImages: item?.props ? serializePanoImages(item?.props) : null,
    })
  ),
});

export const serializeVariantPopupList = (type, data) => {
  switch (type) {
    case VariantListPopupMapping.Filter:
      return {
        title: data?.title,
        type: data?.type,
        list: Object.keys(data?.props)?.map((item) => ({
          title: data?.props?.[item]?.title,
          list: data?.props?.[item]?.props?.map((e) => ({
            key: e?.key,
            title: e?.title,
            type: e?.type,
            value: e?.value,
          })),
        })),
      };
    case VariantListPopupMapping.VariantList:
      return {
        type: data?.type,
      };
  }
};

export const serializeVariantListPopup = (data): VariantListPopupType => ({
  widgetType: data?.type,
  list: data?.props?.map((item) => serializeVariantPopupList(item?.type, item)),
});

export const serializeVariants = (data): VariantListsDataType => ({
  id: data.id,
  name: data?.name,
  variantId: data?.id,
  brandName: data?.brand_name,
  modelName: data?.model_name,
  modelId: data?.model_id,
  emi: {
    title: data?.emi?.title || '',
    subtitle: data?.emi?.sub_title,
    emi: data?.emi?.emi,
    months: data?.emi?.months,
    interestRate: data?.emi?.interest_rate,
    displayValue: data?.emi?.display_value,
    downPayment: data?.emi?.down_payment,
    downPaymentText: data?.emi?.down_payment_text,
  },
  exShowRoomPrice: data?.ex_showroom_price
    ? formatCurrency(data?.ex_showroom_price)
    : '',
  fuelType: data?.fuel,
  onRoadPrice: data?.on_road_price,
  transmissionType: data?.transmission,
  image: getImageKitUrl({
    product: 'ImageKit',
    imageUrl: data?.image || CAR_IMAGE_FALLBACK,
  }),
  waitingPeriod: {
    icon: getImageKitUrl({
      product: 'ImageKit',
      imageUrl: data?.waiting_period?.icon || '',
    }),
    title: data?.waiting_period?.title || '',
  },
  price: {
    title: data?.price?.title,
    subtitle: data?.price?.sub_title,
  },
  fuelMaintenanceRangeSlider: {
    multiplicationFactor:
      data?.fuel_maintenance_range_slider?.multiplication_factor,
  },
  variantListCard: {
    title: data?.variant_list_card?.title,
    subtitle: data?.variant_list_card?.sub_title,
    priceSubtitle: data?.variant_list_card?.price_sub_title,
    priceTitle: data?.variant_list_card?.price_title,
  },
  rto: data?.rto ? formatCurrency(data?.rto) : '',
  insurance: data?.insurance ? formatCurrency(data?.insurance) : '',
  other: data?.other ? formatCurrency(data?.other) : '',
});

export const serializeSimilarModel = (data): SimilarModelListType => ({
  modelId: data?.model_id,
  variantId: data?.variant_id,
  brandName: data?.brand_name,
  modelName: data?.model_name,
  image: getImageKitUrl({
    product: 'ImageKit',
    imageUrl: data?.image,
  }),
  variantListCard: {
    title: data?.variant_list_card?.title,
    subtitle: data?.variant_list_card?.sub_title,
    priceSubtitle: data?.variant_list_card?.price_sub_title,
    priceTitle: data?.variant_list_card?.price_title,
  },
});

export const serializeSimilarModelList = (data) => {
  if (Array.isArray(data)) return null;
  const keys = Object.keys(data || {});
  const obj = {};
  keys?.forEach((item) => {
    obj[item] = data?.[item]?.map((e) => serializeSimilarModel(e));
  });
  return obj;
};

export const serializeCityListPopup = (data): CityListPopupType => {
  const arr = [
    {
      title: 'Search for a city',
      type: 'search',
      list: [],
    },
  ];
  const obj = {
    widgetType: MODEL_WIDGETS.CityListPopup,
    list: [],
  };
  const popularCities = [];
  const otherCities = [];
  data?.forEach?.((item) => {
    const cityData = {
      cityId: item?.id,
      title: item?.name,
      image: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.cover,
      }),
    };
    if (item?.is_popular) {
      popularCities.push(cityData);
    } else {
      otherCities?.push(cityData);
    }
  });
  arr.push({
    type: 'popular_city_list',
    title: 'POPULAR CITIES',
    list: popularCities,
  });
  arr.push({
    type: 'all_city_list',
    title: 'ALL CITIES',
    list: otherCities,
  });

  obj.list = arr;
  return obj;
};

export const serializeVehicleDetails = (data): VehicleDetailsType => ({
  widgetType: data?.type,
  data: {
    alerts: data?.props?.alert_widget?.map(
      (item): VehicleAlertsType => ({
        icon: getImageKitUrl({
          product: 'ImageKit',
          imageUrl: item?.icon || '',
        }),
        title: item?.title || '',
        subtitle: item?.subtitle || '',
      })
    ),
    modelDetails: {
      brandLogo: data?.props?.brand_icon_url || '',
      rating: data?.props?.rating || '',
      modelImage: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: data?.props?.vehicle_icon_url,
      }),
      modelName: data?.props?.vehicle_text || '',
      vehicleNumber: data?.props?.vrn_text || '',
    },
    ownerDetails: {
      title: data?.props?.owner_details?.title || '',
      subtitle: data?.props?.owner_details?.subtitle || '',
      description: data?.props?.owner_details?.description || '',
    },
    registrationDetails: {
      title: data?.props?.registration_details?.title || '',
      subtitle: data?.props?.registration_details?.subtitle || '',
    },
    bottomStrip: {
      title: data?.props?.bottom?.title || '',
      cta: {
        screenId: data?.props?.bottom?.cta?.screen_id,
      },
    },
  },
});

export const serializeRCDetails = (data): RCDetailsType => ({
  widgetType: data?.type,
  title: data?.title,
  list: data?.props?.map(
    (item): AccordionListType => ({
      title: item?.title || '',
      icon: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.icon_url || '',
      }),
      list: item?.data?.map(
        (e): AccordionListDataType => ({
          title: e?.key || '',
          subtitle: e?.value || '',
          description: e?.subtitle || '',
          statusType: e?.type || '',
          statusLabel: e?.status || '',
        })
      ),
    })
  ),
});

export const serializeCityList = (data) => {
  const arr = Object.keys(data || {});
  const obj = {};
  arr.forEach((item) => {
    const variantObj = {};
    const variantKeys = Object.keys(data?.[item] || {});
    variantKeys.forEach((e) => {
      variantObj[e] = {
        exShowroomPrice: data?.[item]?.[e]?.ex_showroom_price,
        insurance: data?.[item]?.[e]?.insurance,
        onRoadPrice: data?.[item]?.[e]?.on_road_price,
        other: data?.[item]?.[e]?.other,
        rto: data?.[item]?.[e]?.rto,
        price: {
          title: data?.[item]?.[e]?.price?.title,
          subtitle: data?.[item]?.[e]?.price?.sub_title,
        },
        emi: {
          title: data?.[item]?.[e]?.emi?.title,
          subtitle: data?.[item]?.[e]?.emi?.sub_title,
          emi: data?.[item]?.[e]?.emi?.emi,
          months: data?.[item]?.[e]?.emi?.months,
          interestRate: data?.[item]?.[e]?.emi?.interest_rate,
          displayValue: data?.[item]?.e?.emi?.display_value,
          downPayment: data?.[item]?.e?.emi?.down_payment,
          downPaymentText: data?.[item]?.e?.emi?.down_payment_text,
        },
      };
    });
    obj[item] = variantObj;
  });
  return obj;
};

export const serializeVariantDetails = (data): VariantDetailsType => ({
  specificationAndFeatures: data?.specification_data?.map(
    (item): VariantSpecificationType => ({
      image: getImageKitUrl({
        product: 'ImageKit',
        imageUrl: item?.image,
      }),
      title: item?.title,
      data: item?.data?.map(
        (e): VariantSpecificationListType => ({
          key: e?.key,
          modelId: e?.model_id,
          specId: e?.spec_id,
          value: e?.value,
          variantId: e?.variant_id,
        })
      ),
    })
  ),
  localeDetails: data?.variant_locale_list
    ? {
        exShowroomPrice: formatCurrency(
          data?.variant_locale_list?.ex_showroom_price
        ),
        insurance: data?.variant_locale_list?.insurance
          ? formatCurrency(data?.variant_locale_list?.insurance)
          : '',
        onRoadPrice: data?.variant_locale_list?.on_road_price,
        other: data?.variant_locale_list?.other
          ? formatCurrency(data?.variant_locale_list?.other)
          : '',
        rto: data?.variant_locale_list?.rto
          ? formatCurrency(data?.variant_locale_list?.rto)
          : '',
        price: {
          title: data?.variant_locale_list?.price?.title,
          subtitle: data?.variant_locale_list?.price?.sub_title,
        },
        emi: {
          title: data?.variant_locale_list?.emi?.title,
          subtitle: data?.variant_locale_list?.emi?.sub_title,
          emi: data?.variant_locale_list?.emi?.emi,
          months: data?.variant_locale_list?.emi?.months,
          interestRate: data?.variant_locale_list?.emi?.interest_rate,
          displayValue: data?.variant_locale_list?.emi?.display_value,
          downPayment: data?.variant_locale_list?.emi?.down_payment,
          downPaymentText: data?.variant_locale_list?.emi?.down_payment_text,
        },
      }
    : null,
});

export const serializeBannerWidget= (data) : BannerWidgetType => ({
  widgetType: data?.type,
  banners: data?.banners?.map((e) => ({
    redirectionUrl: e?.redirection_url,
    bannerUrl: e?.banner_url
  })),
})

export const serializeWidgets = (type, data) => {
  switch (type) {
    case MODEL_WIDGETS.HeroSection:
      return serializeHerosSection(data);
    case MODEL_WIDGETS.MediaIcons:
      return serializeMediaIcons(data);
    case MODEL_WIDGETS.OverView:
      return serializeOverView(data);
    case MODEL_WIDGETS.SpecificationTabBar:
      return serializeSpecificationTabBar(data);
    case MODEL_WIDGETS.KeySpecifications:
      return serializeKeySpecifications(data);
    case MODEL_WIDGETS.ExclusiveFeatures:
      return serializeExclusiveFeatures(data);
    case MODEL_WIDGETS.SpecificationAndFeatures:
      return serializeSpecificationAndFeatures(data);
    case MODEL_WIDGETS.VariantList:
      return serializeVariantList(data);
    case MODEL_WIDGETS.ColorVariant:
      return serializeColorVariant(data);
    case MODEL_WIDGETS.MaintenanceWidget:
      return serializeMaintenanceWidget(data);
    case MODEL_WIDGETS.RatingWidget:
      return serializeRatingWidget(data);
    case MODEL_WIDGETS.MediaPopup:
      return serializeMediaPopup(data);
    case MODEL_WIDGETS.VariantListPopup:
      return serializeVariantListPopup(data);
    case MODEL_WIDGETS.CarComparison:
      return {
        widgetType: data?.type,
        title: data?.title || 'Car Comparison',
        list: data?.props?.map((item) => ({
          model_id: item?.model_id,
          title: item?.title,
          image: item?.image,
        })),
      };
    case MODEL_WIDGETS.CityListPopup:
      return serializeCityListPopup(data);
    case MODEL_WIDGETS.VehicleDetailsWidget:
      return serializeVehicleDetails(data);
    case MODEL_WIDGETS.RCDetailsWidget:
      return serializeRCDetails(data);
    case MODEL_WIDGETS.TestDrive:
      return {
        widgetType: data?.type,
        count: data?.test_drive_booking_count,
      };
    case MODEL_WIDGETS.Story:
      return {
        widgetType: data?.type,
        title: data?.props?.title || '',
        story: data?.props?.story || '',
      };
    case MODEL_WIDGETS.Slider:
      return serializeSlider(data);
    case MODEL_WIDGETS.BannerWidget: 
    return serializeBannerWidget(data)
    default:
      return data;
  }
};

export const serializeModelDetailPage = (data): ModelDetailsType => {
  const obj: ModelDetailsType = {
    list: data?.data?.map((item) => serializeWidgets(item?.type, item)),
    variantList: data?.variant_list?.map((item) => serializeVariants(item)),
    similarModelList: serializeSimilarModelList(data?.variant_model_mapping),
    originalVariantList: data?.variant_list?.map((item) =>
      serializeVariants(item)
    ),
    brandId: data?.brand_id,
    modelId: data?.model_id,
    brochureUrl: data?.brochure_url || '',
  };
  return obj;
};
